/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   JobActivitiesTab.tsx
 *     Feature         :   254-pipeline-monitoring-overhaul (US3) — rewrite of the Spec-206 tab.
 *     Description     :   The run explorer, blueprint-formatted (FR-021): ONE kit DataTable
 *                         with SERVER-SIDE sort on the column headers (FR-022), a filter bar
 *                         carrying the pipeline picker + a multi-select status dropdown with
 *                         counts (FR-018/019) + a recent-window selector (FR-025a), human
 *                         pipeline names shown once (FR-020), a diagnostics-gated CSV export
 *                         (FR-026a), retired excluded by default with a toggle (FR-026), and
 *                         all filter/sort/page state URL-encoded so a link reproduces the view
 *                         (FR-023).
 *     Constitution    :   Principle XLV (kit + tokens), XXXVII (headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-24
 * =========================================================================================
 */
import { useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { Card, DataTable, FilterBar, Input, Toggle, Button, Badge, LoadingState, ErrorState, EmptyState, PaginationControls, PAGE_SIZES } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { usePipelineActivities, usePipelineFilterOptions } from '@/hooks/usePipelineMonitoring';
import { exportRuns } from '@/services/pipelineMonitoringApi';
import type { JobActivity, ActivitiesQuery } from '@shared/types/pipelineMonitoring';
import { RunDetailView } from './RunDetailView';
import { SearchableSelect } from './SearchableSelect';
import { StatusMultiSelect } from './StatusMultiSelect';
import { formatEastern } from '@/utils/dateFormatter';
import { roleSatisfies } from '../../../../../shared/access/roles';

const WINDOWS = [
  { key: '7', label: '7d' }, { key: '30', label: '30d' }, { key: '90', label: '90d' }, { key: 'all', label: 'All' },
] as const;
const DEFAULT_PAGE_SIZE = 25;
type SortKey = NonNullable<ActivitiesQuery['sort']>;

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}
function fmtTime(iso: string | null): string { return iso ? formatEastern(iso) : '—'; }
/** Show the app_instance slug under the name only when it adds information (not a case/format twin). */
function slugDiffers(name: string, slug: string): boolean {
  const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm(name) !== norm(slug);
}
function statusVariant(s: string): 'success' | 'danger' | 'warning' | 'default' {
  const u = s.toUpperCase();
  if (['FAILED', 'FAIL', 'ABANDONED', 'ERROR', 'COMPLETED_WITH_ERRORS'].includes(u)) return 'danger';
  if (u === 'PARTIAL') return 'warning';
  if (s === '(unknown)' || ['NO_OP', 'NOOP', 'SKIPPED'].includes(u)) return 'default';
  return 'success';
}

export function JobActivitiesTab({ initialApp }: { initialApp?: string }): JSX.Element {
  // URL is the single source of filter/sort/page state (FR-023).
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const canExport = roleSatisfies(user?.role, ['Admin', 'Manager', 'Operator']);

  const app = sp.get('app') ?? initialApp ?? '';
  const q = sp.get('q') ?? '';
  const statuses = (sp.get('statuses') ?? '').split(',').filter(Boolean);
  const windowKey = sp.get('win') ?? '30';
  const includeRetired = sp.get('retired') === '1';
  const page = Math.max(1, Number(sp.get('page') ?? '1') || 1);
  const pageSize = (PAGE_SIZES as readonly number[]).includes(Number(sp.get('size'))) ? Number(sp.get('size')) : DEFAULT_PAGE_SIZE;
  const sort = (sp.get('sort') ?? 'startedAt') as SortKey;
  const dir: 'asc' | 'desc' = sp.get('dir') === 'asc' ? 'asc' : 'desc';

  const patch = (next: Record<string, string | null>, resetPage = true): void => {
    const n = new URLSearchParams(sp);
    if (resetPage) n.set('page', '1');
    for (const [k, v] of Object.entries(next)) { if (v === null || v === '') n.delete(k); else n.set(k, v); }
    setSp(n, { replace: true });
  };

  // Hour-anchored: CONTENT-stable across renders within the hour (React Query hashes keys by
  // content ⇒ no refetch loop) yet advances hourly. A raw Date.now() minted a fresh key every
  // render and trapped the query in a perpetual loading state.
  const windowParams = ((): { from: string } => {
    if (windowKey === 'all') return { from: '2000-01-01T00:00:00Z' };
    const hourMs = 3_600_000;
    const anchor = Math.floor(Date.now() / hourMs) * hourMs;
    return { from: new Date(anchor - Number(windowKey) * 86_400_000).toISOString() };
  })();

  const query = {
    page, pageSize,
    app: app || undefined,
    q: q || undefined,
    statuses: statuses.length ? statuses : undefined,
    includeRetired: includeRetired || undefined,
    sort, dir,
    ...windowParams,
  };
  const { data, isLoading, isError, refetch } = usePipelineActivities(query);
  // Scope the status-facet counts to the pipeline/window/search in view (the apps dropdown
  // stays the full catalog — the backend leaves it unscoped). Reuse the memoized `from`.
  const { data: filterOpts } = usePipelineFilterOptions({
    app: app || undefined,
    from: windowParams.from,
    q: q || undefined,
    includeRetired: includeRetired || undefined,
  });
  const appOptions = (filterOpts?.apps ?? []).map((a) => ({ value: a.appInstanceId, label: a.appName }));
  const facets = filterOpts?.statusFacets ?? [];

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(total / pageSize));

  const columns: DataTableColumn<JobActivity>[] = [
    { key: 'appName', header: 'Pipeline', width: '34%', render: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-[var(--color-text-primary)]">{r.appName}</div>
        {slugDiffers(r.appName, r.appInstanceId) && (
          <div className="truncate font-mono text-[11px] text-[var(--color-text-secondary)]">{r.appInstanceId}</div>
        )}
      </div>
    ) },
    { key: 'status', header: 'Status', width: '14%', sortValue: (r) => r.status,
      render: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge> },
    { key: 'startedAt', header: 'Started', width: '22%', sortValue: (r) => r.startedAt, render: (r) => fmtTime(r.startedAt) },
    { key: 'durationMs', header: 'Duration', width: '12%', align: 'right', sortValue: (r) => r.durationMs, render: (r) => fmtDuration(r.durationMs) },
    { key: 'runId', header: 'Run', sortable: false, render: (r) => <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">{r.runId}</span> },
  ];

  const selectedRunId = sp.get('run');

  return (
    <div className="space-y-4">
      <FilterBar
        trailing={
          <div className="flex items-center gap-3">
            <Toggle checked={includeRetired} onChange={(v) => patch({ retired: v ? '1' : null })} label="Show retired" />
            {canExport && (
              <Button size="sm" variant="outline" leftIcon={<Download className="h-3.5 w-3.5" />}
                onClick={() => void exportRuns({
                  ...(app ? { app } : {}), ...(q ? { q } : {}),
                  ...(statuses.length ? { statuses: statuses.join(',') } : {}),
                  ...(includeRetired ? { includeRetired: 'true' } : {}),
                  sort, dir, from: windowParams.from,
                })}>
                Export
              </Button>
            )}
          </div>
        }
      >
        <div className="w-48 sm:w-56">
          <SearchableSelect options={appOptions} value={app} onChange={(v) => patch({ app: v })} placeholder="Pipeline" allLabel="All pipelines" />
        </div>
        <StatusMultiSelect facets={facets} selected={statuses} onChange={(next) => patch({ statuses: next.join(',') || null })} />
        <Input className="w-56" placeholder="Search run ID or error text" value={q} onChange={(e) => patch({ q: e.target.value })} />
        <div className="flex items-center gap-1">
          {WINDOWS.map((w) => (
            <Button key={w.key} size="sm" variant={w.key === windowKey ? 'primary' : 'secondary'} onClick={() => patch({ win: w.key })}>{w.label}</Button>
          ))}
        </div>
      </FilterBar>

      {data?.degraded && (
        <Card className="border-[var(--color-status-warning)]">
          <span className="text-sm text-[var(--color-status-warning)]">Logs database unavailable — {data.reason}</span>
        </Card>
      )}

      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : data.runs.length === 0 ? (
        <EmptyState
          title="No runs match these filters"
          message={`Active filters: window ${WINDOWS.find((w) => w.key === windowKey)?.label ?? windowKey}${app ? `, pipeline “${appOptions.find((o) => o.value === app)?.label ?? app}”` : ''}${statuses.length ? `, status ${statuses.join('/')}` : ''}${q ? `, search “${q}”` : ''}. Widen the window or clear a filter.`}
        />
      ) : (
        <>
          {/* Sort is server-side across the WHOLE matching set (FR-022) — driven by the column
              headers via DataTable's controlled sort. Server pagination below (showPagination=false). */}
          <Card padding="none">
            <DataTable
              columns={columns}
              rows={data.runs}
              rowKey={(r) => r.runId}
              pageSize={pageSize}
              minWidth="44rem"
              showPagination={false}
              sortKey={sort}
              sortDir={dir}
              onSortChange={(key, d) => patch({ sort: key, dir: d }, false)}
              onRowClick={(r) => patch({ run: r.runId }, false)}
            />
          </Card>
          {/* Server-side pagination: the kit PaginationControls carries the page-size selector +
              nav; the disclosure line above states the retroactive-exclusion + window bounds. */}
          {((data.excludedRetiredCount ?? 0) > 0 || data.capped || windowKey !== 'all') && (
            <div className="text-xs text-[var(--color-text-secondary)]">
              {data.capped ? 'Result set capped. ' : ''}
              {(data.excludedRetiredCount ?? 0) > 0 ? `${data.excludedRetiredCount} retired pipeline(s) excluded. ` : ''}
              {windowKey !== 'all' ? 'Older runs beyond the window are not shown — widen the window or choose All.' : ''}
            </div>
          )}
          <PaginationControls
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            start={(page - 1) * pageSize}
            shown={data.runs.length}
            onPage={(p) => patch({ page: String(p) }, false)}
            onPageSize={(s) => patch({ size: String(s) })}
          />
        </>
      )}
      <RunDetailView runId={selectedRunId} onClose={() => patch({ run: null }, false)} />
    </div>
  );
}
